import type { AriaLabelingProps } from '@react-types/shared';
import { createContext, forwardRef, useContext } from 'react';

import { clamp } from '@infinex-private/ts-utils/number';
import type { BorderRadiusKey } from '@infinex/ui-constants';
import { cn } from '@infinex/ui-styles';

import type { ViewProps } from '../layout/View';
import { View } from '../layout/View';

export const layers = [0, 1, 2] as const;
export const layerNames = ['zero', 'one', 'two'] as const;
export const MIN_LAYER = 0;
export const MAX_LAYER = layers.length - 1;
export type SurfaceLayer = (typeof layers)[number];
export type SurfaceLayerName = (typeof layerNames)[number];

export const SurfaceContext = createContext<SurfaceLayer>(0);

export const TONES = [
  'neutral',
  'info',
  'positive',
  'brand',
  'caution',
  'critical',
  'highlight',
] as const;

export type SurfaceProps = {
  /**
   * Optionally override the contextual layer of the surface. Use with caution,
   * this will undermine the component's ability to provide accurate hierarchy when
   * nesting surfaces.
   *
   * @default 1
   */
  layer?: SurfaceLayer;
  /**
   * The tone of a surface provides a visual cue to users about the nature of
   * the information being presented.
   *
   * @default 'neutral'
   */
  tone?: (typeof TONES)[number];
  /**
   * Whether to include a border, matching the `tone` of the surface.
   *
   * @default false
   */
  isBordered?: boolean;
  /**
   * The border radius of the surface's corners.
   *
   * @default false
   */
  rounded?: boolean | BorderRadiusKey | 'full';
} & AriaLabelingProps &
  ViewProps;

/**
 * The Surface is used to render elements on different layers, contextually
 * influencing the appearance of contained components.
 *
 * [Taurus docs: `Surface`](https://ui-sb.app.infinex.dev/?path=/docs/components-surface--docs)
 */
export const Surface = forwardRef<HTMLElement, SurfaceProps>(
  function Surface(props, ref) {
    const {
      className,
      isBordered = false,
      layer: layerProp,
      rounded,
      tone = 'neutral',
      ...otherProps
    } = props;

    const ctx = useContext(SurfaceContext);
    const layer = clamp(layerProp ?? ctx + 1, MIN_LAYER, MAX_LAYER);

    return (
      <SurfaceContext.Provider value={layer as SurfaceLayer}>
        <View
          {...otherProps}
          ref={ref}
          className={cn(
            'bg-surface',
            {
              'bg-surfaceTwo': layer === 2,
              'bg-surfaceBrand': tone === 'brand',
              'bg-surfaceCaution': tone === 'caution',
              'bg-surfaceCritical': tone === 'critical',
              'bg-surfaceHighlight': tone === 'highlight',
              'bg-surfaceInfo': tone === 'info',
              'bg-surfacePositive': tone === 'positive',
            },
            {
              border: isBordered,
              'border-emphasis': layer === 2,
              'border-brand': isBordered && tone === 'brand',
              'border-caution': isBordered && tone === 'caution',
              'border-critical': isBordered && tone === 'critical',
              'border-highlight': isBordered && tone === 'highlight',
              'border-info': isBordered && tone === 'info',
              'border-positive': isBordered && tone === 'positive',
            },
            {
              'rounded-none': rounded === false,
              'rounded-xsmall': rounded === 'xsmall',
              'rounded-small': rounded === 'small',
              'rounded-base': rounded === true,
              'rounded-medium': rounded === 'medium',
              'rounded-large': rounded === 'large',
              'rounded-xlarge': rounded === 'xlarge',
              'rounded-full': rounded === 'full',
            },
            className
          )}
        />
      </SurfaceContext.Provider>
    );
  }
);

export function useSurfaceLayer() {
  const layer = useContext(SurfaceContext);
  return layerNames[layer];
}
